import { supabase } from '../lib/supabase';

const INVITE_FUNCTION_NAME = 'invite-user';
const DEFAULT_INVITE_TIMEOUT_MS = 90000;
const RECONCILE_ATTEMPTS = 5;
const RECONCILE_DELAY_MS = 1500;
const SUCCESS_STATUSES = new Set(['invitation_sent', 'opened', 'activated']);

const getInviteRequestTimeoutMs = () => {
  if (typeof window === 'undefined') return DEFAULT_INVITE_TIMEOUT_MS;
  const override = Number(window.__CV80_INVITE_TIMEOUT_MS);
  return Number.isFinite(override) && override > 0 ? override : DEFAULT_INVITE_TIMEOUT_MS;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getFunctionUrl = () => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  if (!supabaseUrl) throw new Error('Supabase URL is not configured.');
  return `${supabaseUrl.replace(/\/$/, '')}/functions/v1/${INVITE_FUNCTION_NAME}`;
};

const readResponseJson = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return {};
  }
};

const createTimeoutError = () =>
  new Error(
    'The invitation request timed out. I am checking whether Supabase completed it before reporting a failure.'
  );

const isTimeoutError = (error) =>
  error?.name === 'AbortError' || /timed out/i.test(error?.message || '');

export const shouldResetExistingForInvite = (row) => {
  const role = row?.portal_role || row?.role || 'member';
  if (role === 'admin') return false;
  const hasLinkedAuthUser = Boolean(
    row?.auth_user_id || row?.profile?.id || (row?.type === 'staff' && row?.id)
  );
  return hasLinkedAuthUser && ['activated', 'attention'].includes(row?.status);
};

export const buildInvitePayload = ({ row, email, hoaId }) => ({
  email,
  full_name: row.displayName || row.member_name || row.full_name || email,
  first_name: row.portal_first_name || row.first_name || row.profile?.first_name || '',
  last_name: row.portal_last_name || row.last_name || row.profile?.last_name || '',
  phone: row.portal_phone || row.roster_phone || row.phone || '',
  role: row.portal_role || row.role || 'member',
  hoa_id: hoaId,
  invitation_id: row.invitation_id,
  invitation_ids: row.invitation_ids || (row.invitation_id ? [row.invitation_id] : []),
  property_id: row.property_id,
  link_type: row.link_type || (row.property_id ? 'owner' : null),
  // Only a real auth account id belongs here. Never fall back to row.id: for
  // invitation-backed rows that is the member_invitations id, which made
  // invite-user fail to resolve an auth user and 400 on pending resends.
  auth_user_id: row.auth_user_id || row.profile?.id || null,
  reset_existing: shouldResetExistingForInvite(row),
  is_acc_member: row.is_acc_member === true,
});

export const invokeInviteUser = async (payload, timeoutMs = getInviteRequestTimeoutMs()) => {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;

  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) throw new Error('You must be signed in before sending invitations.');

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(getFunctionUrl(), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: process.env.REACT_APP_SUPABASE_ANON_KEY || '',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    const data = await readResponseJson(response);
    if (!response.ok || data?.error) {
      throw new Error(data?.error || `Invite request failed with status ${response.status}`);
    }
    return data;
  } catch (error) {
    if (error?.name === 'AbortError') throw createTimeoutError();
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const reconcileInvitationStatus = async ({ row, email, hoaId, startedAt }) => {
  const startedAtMs = startedAt.getTime() - 2000;

  for (let attempt = 0; attempt < RECONCILE_ATTEMPTS; attempt += 1) {
    if (attempt > 0) await sleep(RECONCILE_DELAY_MS);

    let query = supabase
      .from('member_invitations')
      .select('id, status, invited_at, email')
      .eq('hoa_id', hoaId);

    if (row.invitation_id) {
      query = query.eq('id', row.invitation_id);
    } else if (row.property_id) {
      query = query.eq('property_id', row.property_id).ilike('email', email);
    } else {
      query = query.ilike('email', email);
    }

    const { data, error } = await query.maybeSingle();
    if (error) continue;

    const invitedAtMs = data?.invited_at ? new Date(data.invited_at).getTime() : 0;
    const sentDuringRequest = invitedAtMs >= startedAtMs;
    // invite-user only stamps invitation_sent + invited_at AFTER the invite
    // email was accepted by Resend (and revokes the token otherwise), so a
    // fresh invited_at with a success status proves the send completed. No
    // auth account exists until the homeowner redeems the link.
    if (SUCCESS_STATUSES.has(data?.status) && sentDuringRequest) {
      return {
        success: true,
        reconciled: true,
        user_id: null,
        invitation_id: data.id,
        status: data.status,
      };
    }
  }

  return null;
};

export const sendInvitation = async ({ row, email, hoaId }) => {
  const payload = buildInvitePayload({ row, email, hoaId });
  const startedAt = new Date();

  try {
    return await invokeInviteUser(payload);
  } catch (error) {
    if (isTimeoutError(error)) {
      const reconciled = await reconcileInvitationStatus({ row, email, hoaId, startedAt });
      if (reconciled) return reconciled;
    }
    throw error;
  }
};
